'use client';

import { useEffect, useRef, useState } from 'react';
import Script from 'next/script';

import { Skeleton } from '@common/atoms/Skeleton';
import { EmbedConsentGuard } from '@web/atoms/EmbedConsentGuard';
import { useEmbedConsented } from '@web/hooks/useEmbedConsented';

import styles from './XPost.module.scss';

declare global {
    interface Window {
        twttr?: {
            widgets?: {
                createTweet: (postID: string, element: HTMLElement) => Promise<void>;
            };
        };
    }
}

interface Props {
    data: { postID?: string };
}

export const XPost = ({ data: { postID = '' } }: Props) => {
    const [loadingSDK, setLoadingSDK] = useState(true);
    const frameRef = useRef<HTMLDivElement | null>(null);
    const { consented, loadingConsent } = useEmbedConsented('twitter');

    useEffect(() => {
        const intervalID = setInterval(async () => {
            if (consented && frameRef.current && window?.twttr?.widgets?.createTweet) {
                clearInterval(intervalID);
                await window?.twttr?.widgets?.createTweet(postID, frameRef.current);
                setLoadingSDK(false);
            }
        }, 100);
        return () => {
            clearInterval(intervalID);
        };
    }, [consented, postID]);

    return (
        <div className={styles['x-post']} data-testid="article-block-x-post-block">
            {loadingConsent || (consented && loadingSDK) ? <Skeleton className={styles['skeleton']} /> : null}
            {!consented && !loadingConsent ? <EmbedConsentGuard vendor="twitter" /> : null}
            {consented ? (
                <>
                    <Script src="https://platform.twitter.com/widgets.js" strategy="lazyOnload" />
                    <div className={styles['frame']} ref={frameRef} />
                </>
            ) : null}
        </div>
    );
};
